import { __assign } from "tslib";
import { FiasFields, FiasSchema } from '../types';
import { FiasLogger } from '../logger/FiasLogger';
import { fetch } from '@skbkontur/react-ui/lib/net/fetch';
import { FiasAPIResultFactory } from './FiasAPIResultFactory';

var FiasAPI =
/** @class */
function () {
  function FiasAPI(baseUrl, version, fetchFn, defaultQuery) {
    var _this = this;

    if (baseUrl === void 0) {
      baseUrl = '';
    }

    if (fetchFn === void 0) {
      fetchFn = fetch;
    }

    if (defaultQuery === void 0) {
      defaultQuery = {};
    }

    this.baseUrl = baseUrl;
    this.version = version;
    this.fetchFn = fetchFn;
    this.defaultQuery = defaultQuery;
    this.regionsPromise = null;

    this.verify = function (address) {
      var query = __assign(__assign({}, _this.defaultQuery), {
        directParent: true,
        search: false
      });

      return _this.send("verify?" + FiasAPI.createQuery(query), {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify([address.convertForVerification()])
      }).then(function (_a) {
        var success = _a.success,
            data = _a.data,
            error = _a.error;

        if (success && data) {
          var _b = data[0] || {},
              _c = _b.address,
              verifiedAddress = _c === void 0 ? {} : _c,
              _d = _b.isValid,
              isValid = _d === void 0 ? false : _d,
              invalidLevel = _b.invalidLevel;

          return FiasAPIResultFactory.success(__assign({
            address: verifiedAddress,
            isValid: isValid
          }, invalidLevel ? {
            invalidLevel: invalidLevel.toLowerCase()
          } : {}));
        } else {
          return FiasAPIResultFactory.fail(error && error.message);
        }
      });
    };

    this.search = function (_a) {
      var fiasId = _a.fiasId,
          searchText = _a.searchText,
          field = _a.field,
          parentFiasId = _a.parentFiasId,
          limit = _a.limit,
          fullAddress = _a.fullAddress,
          directParent = _a.directParent;

      var query = __assign(__assign({}, _this.defaultQuery), {
        prefix: searchText,
        actual: true,
        parentFiasId: parentFiasId,
        limit: limit,
        fullAddress: fullAddress,
        directParent: directParent
      });

      var emptyResult = {
        success: true,
        status: 200,
        data: []
      };

      if (fiasId) {
        return _this.resolveFiasId(fiasId);
      }

      if (searchText) {
        if (!field) {
          return _this.resolveAddress(searchText, limit);
        } else {
          switch (field) {
            case FiasFields.region:
              return _this.searchRegions(searchText);

            case FiasFields.house:
              return _this.searchHouse(__assign(__assign({}, query), {
                schema: FiasSchema.Fias
              }));

            case FiasFields.garhouse:
              return _this.searchHouse(__assign(__assign({}, query), {
                schema: FiasSchema.Gar
              }));

            case FiasFields.stead:
              return _this.searchStead(query);

            case FiasFields.room:
              return _this.searchRoom(query);

            default:
              return _this.searchAddressObject(__assign(__assign({}, query), {
                levels: [field]
              }));
          }
        }
      }

      return Promise.resolve(emptyResult);
    };

    this.searchCountry = function (query) {
      return _this.send("countries?" + FiasAPI.createQuery(query));
    };

    this.send = function (path, params) {
      if (params === void 0) {
        params = {};
      }

      var resultPromise = _this.baseUrl ? _this.fetchFn("" + _this.baseUrl + path, __assign({
        method: 'GET'
      }, params)).then(function (result) {
        return result.ok ? result.json().then(function (data) {
          return FiasAPIResultFactory.success(data);
        }) : Promise.reject(new Error(FiasLogger.warnings.fetchError));
      }) : Promise.reject(new Error(FiasLogger.warnings.noBaseUrl));
      return resultPromise.catch(function (_a) {
        var message = _a.message;
        FiasLogger.log(message);
        return FiasAPIResultFactory.fail(message);
      });
    };

    this.resolveFiasId = function (fiasId) {
      return _this.send("addresses/structural/" + fiasId + "?" + FiasAPI.createQuery(_this.defaultQuery)).then(function (result) {
        var success = result.success,
            data = result.data,
            error = result.error;

        if (success && data) {
          return FiasAPIResultFactory.success([data]);
        } else {
          return FiasAPIResultFactory.fail(error && error.message);
        }
      });
    };

    this.searchAddressObject = function (query) {
      return _this.send("addresses?" + FiasAPI.createQuery(query));
    };

    this.resolveAddress = function (address, limit, level) {
      if (level === void 0) {
        level = FiasFields.apartment;
      }

      return _this.send("addresses/search?" + FiasAPI.createQuery(__assign(__assign({}, _this.defaultQuery), {
        address: address,
        limit: limit,
        level: level
      })));
    };

    this.searchStead = function (query) {
      return _this.send("steads?" + FiasAPI.createQuery(query)).then(function (result) {
        var success = result.success,
            data = result.data,
            error = result.error;

        if (success && data) {
          return FiasAPIResultFactory.success(data.map(function (stead) {
            return {
              stead: stead
            };
          }));
        } else {
          return FiasAPIResultFactory.fail(error && error.message);
        }
      });
    };

    this.searchHouse = function (query) {
      return _this.send("houses?" + FiasAPI.createQuery(query)).then(function (result) {
        var success = result.success,
            data = result.data,
            error = result.error;

        if (success && data) {
          return FiasAPIResultFactory.success(data.map(function (house) {
            var _a;

            return _a = {}, _a[query.schema === FiasSchema.Gar ? FiasFields.garhouse : FiasFields.house] = house, _a;
          }));
        } else {
          return FiasAPIResultFactory.fail(error && error.message);
        }
      });
    };

    this.searchRoom = function (query) {
      return _this.send("rooms?" + FiasAPI.createQuery(query)).then(function (result) {
        var success = result.success,
            data = result.data,
            error = result.error;

        if (success && data) {
          return FiasAPIResultFactory.success(data.map(function (room) {
            return {
              room: room
            };
          }));
        } else {
          return FiasAPIResultFactory.fail(error && error.message);
        }
      });
    };

    this.searchRegions = function (searchText) {
      if (!_this.regionsPromise) {
        _this.regionsPromise = _this.send('addresses/regions');
      }

      if (!searchText) {
        return _this.regionsPromise;
      }

      var isStartsWithSearchText = function isStartsWithSearchText(str) {
        return str && str.toLowerCase().includes(searchText.toLowerCase());
      };

      return _this.regionsPromise.then(function (result) {
        var success = result.success,
            data = result.data;

        if (success && data) {
          return FiasAPIResultFactory.success(data.filter(function (address) {
            var _a = address.region,
                name = _a.name,
                _b = _a.code,
                code = _b === void 0 ? '' : _b;
            return isStartsWithSearchText(name) || isStartsWithSearchText(code);
          }));
        }

        return result;
      });
    };

    if (version) {
      this.defaultQuery = __assign(__assign({}, defaultQuery), {
        version: version
      });
    }
  }

  FiasAPI.createQuery = function (query) {
    var params = [];

    for (var key in query) {
      if (Object.prototype.hasOwnProperty.call(query, key)) {
        var param = query[key];

        if (param !== undefined) {
          if (key === 'levels' && Array.isArray(param)) {
            for (var _i = 0, param_1 = param; _i < param_1.length; _i++) {
              var level = param_1[_i];
              params.push("level[]=" + encodeURIComponent(level));
            }
          } else {
            params.push(key + "=" + encodeURIComponent("" + param));
          }
        }
      }
    }

    return params.join('&');
  };

  return FiasAPI;
}();

export { FiasAPI };